import { isEmpty, pick } from 'ramda';
import { createAction } from 'redux-actions';

import { identifyLoggedUser, logger } from '#src/lib/logger';
import { SESSION_STORAGE_KEY } from '#src/lib/session-storage-keys';
import { zE } from '#src/lib/zendesk';
import { isServer } from '#src/lib/utils.mjs';

import { identify } from './identify.js';

export const SESSION_GEO_LOCATION = 'SESSION_GEO_LOCATION';
export const SESSION_HYDRATE = 'SESSION_HYDRATE';
export const SESSION_LOGOUT = 'SESSION_LOGOUT';
export const SESSION_REFRESH = 'SESSION_REFRESH';
export const SESSION_REMOVAL = 'SESSION_REMOVAL';
export const SESSION_REMOVAL_PROSPECT_USER = 'SESSION_REMOVAL_PROSPECT_USER';
export const SESSION_SET_AUTH_TOKEN = 'SESSION_SET_AUTH_TOKEN';
export const SESSION_SET_MEMBERSHIP_RETURN_GAME =
  'SESSION_SET_MEMBERSHIP_RETURN_GAME';
export const SESSION_SET_PROSPECT_USER = 'SESSION_SET_PROSPECT_USER';
export const SESSION_SET_USER = 'SESSION_SET_USER';

const unregisterUserLogging = (resetAnalytics = false) => {
  !isServer && resetAnalytics && analytics.reset();
  zE('webWidget', 'reset');
};

const registerUserLogging = (user) => {
  if (isServer || !user?.id) return {};
  const name = user.displayName;
  try {
    identify(user);
    identifyLoggedUser(user.id); // Sentry
    zE('webWidget', 'identify', {
      name,
      email: user.email,
    });
    zE('webWidget', 'prefill', {
      name: {
        value: name,
        readOnly: true,
      },
      email: {
        value: user.email,
      },
    });
    // log help widget events to segment
    zE('webWidget:on', 'userEvent', (event) => {
      analytics.track(
        ['Help Widget', event?.action].filter(Boolean).join(' - '),
        event
      );
    });
  } catch (error) {
    logger.info(error, {
      message:
        'Unable to initialize tracking user data. Are you on the server?',
    });
  }
};

export const sessionRemoval = createAction(SESSION_REMOVAL, () => {
  unregisterUserLogging();
});

export const setAuthToken = createAction(SESSION_SET_AUTH_TOKEN);

export const setMembershipReturnGame = createAction(
  SESSION_SET_MEMBERSHIP_RETURN_GAME
);

export const hydrateSessionState = createAction(SESSION_HYDRATE, () => {
  const rawSession = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

  try {
    const session = JSON.parse(rawSession);
    return session;
  } catch {
    return {};
  }
});

export const clearProspectUser = createAction(
  SESSION_REMOVAL_PROSPECT_USER,
  () => {
    // do not clear analytics when transitioning between prospect and real user,
    // regardless of whether it's ultimately the same user id or not, otherwise
    // the anon id will change, making analytics inconsistent.
    unregisterUserLogging(false);
  }
);

const selectSessionUserAttributes = pick([
  'id',
  'email',
  'firstName',
  'lastName',
  'displayName',
  'role',
]);

export const setProspectUser = createAction(
  SESSION_SET_PROSPECT_USER,
  (user) => {
    if (!user?.id) return {};
    registerUserLogging(user);
    return selectSessionUserAttributes(user);
  }
);

/**
 * Set the authenticated user.
 */
export const setSessionUser = createAction(SESSION_SET_USER, (user) => {
  if (!user?.id) return {};
  const fallbackName = `${user.firstName || ''} ${user.lastName || ''}`?.trim();
  const name = user.displayName || fallbackName;

  registerUserLogging(user);

  let sessionUser = selectSessionUserAttributes(user);
  return isEmpty(name) ? sessionUser : { ...sessionUser, name };
});

/**
 * Set user geo location
 */
export const setGeoLocation = createAction(
  SESSION_GEO_LOCATION,
  (geoLocation) => geoLocation
);
